<template>
  <Card class="md-table-card mt-3">
    <template #title>
      <div class="flex justify-content-between align-items-center flex-wrap gap-2">
        <span class="md-table-title">{{ title }}</span>
        <span class="text-xs text-500">{{ subtitle }}</span>
      </div>
    </template>
    <template #content>
      <div v-if="error" class="md-empty">
        {{ error }}
      </div>
      <div v-else>
        <div class="md-table-controls" v-if="showControls">
          <div v-if="filterable" class="md-search">
            <i class="pi pi-search text-xs text-500"></i>
            <input
              v-model="searchTerm"
              type="text"
              class="md-search-input"
              :placeholder="filterPlaceholder"
            />
          </div>
          <div class="md-control-spacer"></div>
          <div v-if="activeSortLabel" class="md-sort-label">
            Sorted by <span class="md-sort-label__value">{{ activeSortLabel }}</span>
          </div>
          <div v-if="periodOptions.length" class="md-period-wrapper">
            <Dropdown
              v-model="periodModel"
              :options="periodOptions"
              option-label="label"
              option-value="value"
              class="md-period-dropdown"
            />
          </div>
        </div>
        <div class="table-responsive">
          <table class="md-table">
            <thead>
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.field"
                  :class="{
                    'md-sortable': column.sortable,
                    'md-sorted': column.sortable && column.field === sortField,
                  }"
                  @click="toggleSort(column)"
                >
                  <span class="md-header-content">
                    {{ column.label }}
                    <i
                      v-if="column.sortable"
                      class="pi md-sort-icon"
                      :class="sortIconClass(column.field)"
                    ></i>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr
                  v-for="index in skeletonRowCount"
                  :key="`skeleton-${index}`"
                >
                  <td
                    v-for="column in columnCount"
                    :key="`skeleton-${index}-${column}`"
                  >
                    <Skeleton height="1.2rem" />
                  </td>
                </tr>
              </template>
              <template v-else-if="paginatedRows.length">
                <tr v-for="row in paginatedRows" :key="row.__key">
                  <td
                    v-for="column in columns"
                    :key="`${row.__key}-${column.field}`"
                  >
                    {{ row[column.field] }}
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td :colspan="columnCount" class="md-empty-row">
                  {{ emptyMessage }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="md-pagination" v-if="!loading && totalPages > 1">
          <button
            class="md-page-btn"
            type="button"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            Prev
          </button>
          <span class="md-page-indicator">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button
            class="md-page-btn"
            type="button"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch } from "vue";
import Card from "primevue/card";
import Dropdown from "primevue/dropdown";
import Skeleton from "primevue/skeleton";

const SORT_ASC = "asc";
const SORT_DESC = "desc";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
  loadingMessage: {
    type: String,
    default: "Loading data...",
  },
  emptyMessage: {
    type: String,
    default: "No data for this range.",
  },
  rows: {
    type: Array,
    default: () => [],
  },
  columns: {
    type: Array,
    default: () => [],
  },
  periodOptions: {
    type: Array,
    default: () => [],
  },
  selectedPeriod: {
    type: String,
    default: "",
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  filterable: {
    type: Boolean,
    default: false,
  },
  filterPlaceholder: {
    type: String,
    default: "Search…",
  },
  defaultSortField: {
    type: String,
    default: "",
  },
  defaultSortOrder: {
    type: String,
    default: SORT_DESC,
    validator: (value) => [SORT_ASC, SORT_DESC, ""].includes(value),
  },
});

const emit = defineEmits(["update:period"]);

const currentPage = ref(1);
const skeletonRowCount = 6;
const searchTerm = ref("");
const sortField = ref("");
const sortOrder = ref("");

const showControls = computed(
  () => props.filterable || (props.periodOptions?.length ?? 0) > 0
);

const columnCount = computed(() => Math.max(props.columns.length || 0, 3));

const sortableColumns = computed(() =>
  (props.columns || []).filter((column) => column?.sortable)
);

const processedRows = computed(() => {
  const baseRows = Array.isArray(props.rows) ? props.rows : [];
  if (!props.filterable) return baseRows;
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return baseRows;
  return baseRows.filter((row = {}) =>
    (props.columns || []).some((column = {}) => {
      const value = row[column.field];
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(term);
    })
  );
});

const sortedRows = computed(() => {
  if (!sortField.value) return processedRows.value;
  const order = sortOrder.value === SORT_ASC ? 1 : -1;
  return [...processedRows.value].sort((a, b) => {
    const aValue = a?.[sortField.value];
    const bValue = b?.[sortField.value];
    if (aValue === bValue) return 0;

    const aNum = Number(aValue);
    const bNum = Number(bValue);
    const aIsNum = !Number.isNaN(aNum);
    const bIsNum = !Number.isNaN(bNum);

    if (aIsNum && bIsNum) {
      return aNum > bNum ? order : -order;
    }

    const aStr = String(aValue ?? "").toLowerCase();
    const bStr = String(bValue ?? "").toLowerCase();
    if (aStr === bStr) return 0;
    return aStr > bStr ? order : -order;
  });
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(sortedRows.value.length / props.pageSize))
);

const paginatedRows = computed(() => {
  const start = (currentPage.value - 1) * props.pageSize;
  const end = start + props.pageSize;
  return sortedRows.value.slice(start, end);
});

const goToPage = (page) => {
  const safePage = Math.min(Math.max(page, 1), totalPages.value);
  currentPage.value = safePage;
};

const periodModel = computed({
  get: () => props.selectedPeriod,
  set: (value) => {
    emit("update:period", value);
  },
});

const applyDefaultSort = () => {
  sortField.value = "";
  sortOrder.value = "";
  const defaultField = props.defaultSortField;
  if (!defaultField) return;
  const column = (props.columns || []).find(
    (col) => col.field === defaultField && col.sortable
  );
  if (!column) return;
  sortField.value = column.field;
  sortOrder.value =
    column.defaultSortOrder || props.defaultSortOrder || SORT_DESC;
};

const toggleSort = (column) => {
  if (!column?.sortable) return;
  if (sortField.value !== column.field) {
    sortField.value = column.field;
    sortOrder.value = column.defaultSortOrder || SORT_DESC;
    return;
  }
  sortOrder.value =
    sortOrder.value === SORT_DESC ? SORT_ASC : SORT_DESC;
};

const sortIconClass = (field) => {
  if (sortField.value !== field) return "pi-sort-alt";
  return sortOrder.value === SORT_ASC ? "pi-sort-amount-up" : "pi-sort-amount-down";
};

const activeSortLabel = computed(() => {
  if (!sortField.value) return "";
  const column = sortableColumns.value.find((col) => col.field === sortField.value);
  if (!column) return "";
  const direction = sortOrder.value === SORT_ASC ? "asc" : "desc";
  return `${column.label} (${direction})`;
});

watch(
  () => props.rows,
  () => {
    currentPage.value = 1;
    if (props.filterable) {
      searchTerm.value = "";
    }
    applyDefaultSort();
  },
  { immediate: true }
);

watch(
  () => props.selectedPeriod,
  () => {
    currentPage.value = 1;
  }
);

watch(searchTerm, () => {
  currentPage.value = 1;
});
</script>

<style scoped>
.md-table-title {
  font-weight: 600;
}

.md-table-controls {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.md-period-wrapper {
  flex: 0 0 auto;
}

.md-period-dropdown {
  min-width: 12rem;
}

.md-search {
  flex: 1 1 auto;
  min-width: 12rem;
  max-width: 22rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 999px;
  background: var(--surface-card);
}

.md-sort-label {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.md-sort-label__value {
  font-weight: 600;
  color: var(--text-color);
}

.md-control-spacer {
  flex: 1 1 auto;
}

.md-sort-label,
.md-period-wrapper {
  flex: 0 0 auto;
}

.md-search-input {
  border: none;
  outline: none;
  width: 100%;
  background: transparent;
  font-size: 0.85rem;
  color: var(--text-color);
}

.md-search-input::placeholder {
  color: var(--text-color-secondary);
}

.md-search-input:focus {
  outline: none;
}

.table-responsive {
  min-height: 220px;
}

.md-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.md-table thead {
  background: var(--surface-100);
}

.md-table th,
.md-table td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
  text-align: left;
}

.md-table th {
  user-select: none;
}

.md-header-content {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.md-sortable {
  cursor: pointer;
}

.md-sort-icon {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.md-sorted .md-sort-icon {
  color: var(--primary-color);
}

.md-empty-row {
  text-align: center;
  padding: 1.5rem 0;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}

.md-empty {
  border: 1px dashed var(--surface-border);
  border-radius: 12px;
  padding: 1.25rem;
  text-align: center;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  background: var(--surface-ground);
}

.md-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.md-page-btn {
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-card);
  color: var(--text-color);
  cursor: pointer;
  font-size: 0.85rem;
}

.md-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.md-page-indicator {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}
</style>
